import { useEffect, useState } from "react";
import type { ReactNode } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import type { PlannedQuestion } from "@talenthop/shared";
import { api } from "../../lib/api.js";
import { useVerticals } from "./useVerticals.js";
import { QuestionPlanEditor } from "./QuestionPlanEditor.js";
import { CompetencyPicker } from "../../components/CompetencyPicker.js";
import { Briefcase, Check, Alert } from "../../components/icons.js";
import { X } from "lucide-react";

/**
 * The requisition-wide question plan editor — opened from the requisitions list row menu and from
 * `/app/requisitions/:id?questions=1`. This used to carry its own flat, competency-blind question
 * list (a read-only "Competency: X" label, a hardcoded `competencyId: "technical"` on Add, and a
 * save-error handler that showed "Saved!" and closed the modal even when the PUT failed). It now
 * wraps `QuestionPlanEditor` — the flat, numbered question editor — and owns only the
 * modal chrome, the requisition switcher, and a real Save that reports a real failure.
 */

interface RequisitionOption {
  id: string;
  title: string;
  questionCount?: number;
}

interface Props {
  requisitions: RequisitionOption[];
  initialRequisitionId: string;
  onClose: () => void;
}

/** Just enough of the requisition to resolve its vertical + selected competencies — every other
 *  screen (RequisitionDetail's Weights panel) resolves the same two fields off this same endpoint. */
interface FunnelRequisitionLite {
  id: string;
  verticalKey?: string | null;
  config: { competencies?: string[] };
}

export function RequirementQuestionsModal({
  requisitions,
  initialRequisitionId,
  onClose,
}: Props): ReactNode {
  const queryClient = useQueryClient();
  const selectedReqId = initialRequisitionId;
  const { byKey } = useVerticals();

  const reqQuery = useQuery({
    queryKey: ["funnel", selectedReqId],
    queryFn: () => api.get<{ requisition: FunnelRequisitionLite }>(`/requisitions/${selectedReqId}/funnel`),
    enabled: Boolean(selectedReqId),
  });
  const req = reqQuery.data?.requisition;
  const vertical = byKey(req?.verticalKey);
  const competencies = req?.config.competencies ?? [];

  // The editor's own edit buffer, mirrored up here so this wrapper can PUT it. `dirty` tracks
  // whether there's an unsaved hand-edit; a Regenerate already persisted server-side (source
  // becomes "generated"), so it clears dirty rather than requiring a second Save for the same set.
  const [draft, setDraft] = useState<PlannedQuestion[] | null>(null);
  const [dirty, setDirty] = useState(false);
  const [status, setStatus] = useState<{ tone: "good" | "crit"; text: string } | null>(null);
  // The stored plan (same cache entry the editor reads), so Save knows there is something to keep
  // before the recruiter has touched anything.
  const storedPlan = useQuery({
    queryKey: ["question-plan", selectedReqId],
    queryFn: () => api.get<{ questions: PlannedQuestion[] }>(`/requisitions/${selectedReqId}/questions`),
    enabled: Boolean(selectedReqId),
    staleTime: Infinity,
    refetchOnWindowFocus: false,
    retry: false,
  });
  // Save is available whenever there are valid questions — after a Regenerate too (it used to stay
  // locked because Regenerate had already stored the set). Disabled only when nothing valid is left.
  const shownQuestions = draft ?? storedPlan.data?.questions ?? [];
  const hasValidQuestions = shownQuestions.some((q) => q.prompt.trim().length > 0);

  const save = useMutation({
    mutationFn: (questions: PlannedQuestion[]) =>
      api.put<{ questions: PlannedQuestion[] }>(`/requisitions/${selectedReqId}/questions`, { questions }),
    onSuccess: () => {
      setDirty(false);
      setStatus({ tone: "good", text: "Saved." });
      void queryClient.invalidateQueries({ queryKey: ["question-plan", selectedReqId] });
      void queryClient.invalidateQueries({ queryKey: ["requisitions"] });
      setTimeout(() => {
        setStatus(null);
        onClose();
      }, 700);
    },
    // A failed save must look like a failed save: no checkmark, no closing the editor out from
    // under an edit the recruiter hasn't actually gotten to keep. Bug fix (was previously treated
    // identically to success — see the plan this modal was rewritten from).
    onError: (err: unknown) => {
      setStatus({
        tone: "crit",
        text: err instanceof Error ? err.message : "Couldn't save the questions — try again.",
      });
    },
  });

  const currentRequisition = requisitions.find((r) => r.id === selectedReqId);
  const reqTitle = currentRequisition?.title || "Selected Requisition";

  // First-time setup: a requisition with nothing chosen yet gets a suggested selection — every
  // competency its own vertical defines, capped at the server's max(12) — which the recruiter can
  // then narrow with the same chip picker used everywhere else. Seeded once per (vertical, empty
  // selection) pair, not on every render, so toggling a chip doesn't get stomped by the effect.
  const [pickerSelection, setPickerSelection] = useState<string[]>([]);
  useEffect(() => {
    if (vertical && competencies.length === 0 && pickerSelection.length === 0) {
      setPickerSelection(vertical.competencies.map((c) => c.competencyId).slice(0, 12));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [vertical, competencies.length]);

  const setCompetencies = useMutation({
    mutationFn: (chosen: string[]) =>
      api.post<{ questions: PlannedQuestion[] }>(`/requisitions/${selectedReqId}/questions/generate`, {
        competencies: chosen,
      }),
    onSuccess: () => {
      setStatus({ tone: "good", text: "Competencies set — questions generated." });
      void queryClient.invalidateQueries({ queryKey: ["funnel", selectedReqId] });
      void queryClient.invalidateQueries({ queryKey: ["question-plan", selectedReqId] });
      void queryClient.invalidateQueries({ queryKey: ["requisitions"] });
    },
    onError: (err: unknown) => {
      setStatus({
        tone: "crit",
        text: err instanceof Error ? err.message : "Couldn't set competencies — try again.",
      });
    },
  });

  return (
    <div className="customer-modal-backdrop t1-scrim" onClick={onClose}>
      <div
        className="questions-modal-card t1-modal"
        onClick={(e) => e.stopPropagation()}
        role="dialog"
        aria-modal="true"
        aria-label="Interview questions"
      >
        {/* Header */}
        <div className="questions-modal-header">
          <div className="modal-title-box">
            <div className="modal-icon-badge" style={{ background: "var(--teal-wash)", color: "var(--teal)" }}>
              <Briefcase style={{ width: 20, height: 20 }} />
            </div>
            <div>
              {/* The popup belongs to one requirement — named here, not picked from a dropdown. */}
              <h3 className="modal-title">Interview questions · {reqTitle}</h3>
              <p className="modal-sub">
                What the AI interviewer asks every candidate on this requirement who has no tailored plan.
              </p>
            </div>
          </div>
          <button
            type="button"
            className="t1-icon-btn"
            onClick={onClose}
            aria-label="Close"
            data-tip="Close"
            data-tip-pos="left"
          >
            <X aria-hidden="true" />
          </button>
        </div>

        {/* Modal Content Body */}
        <div className="questions-modal-body">
          {reqQuery.isLoading ? (
            <div style={{ padding: 48, textAlign: "center", color: "var(--ink-mute)" }}>
              Loading configured questions...
            </div>
          ) : reqQuery.isError ? (
            <div className="banner warn">
              <Alert />
              <div style={{ flex: 1 }}>
                Couldn't load {reqTitle}'s vertical and competencies.
                <button
                  className="btn ghost sm"
                  style={{ marginLeft: 10 }}
                  onClick={() => void reqQuery.refetch()}
                >
                  Retry
                </button>
              </div>
            </div>
          ) : competencies.length === 0 ? (
            <div>
              <p className="mut sm" style={{ margin: "0 0 10px" }}>
                No assessment competencies are configured yet. Suggested below, from the{" "}
                <b>{vertical?.name ?? req?.verticalKey}</b> vertical — untoggle anything that
                doesn't apply to this role, then generate the interview questions.
              </p>
              <CompetencyPicker vertical={vertical} selected={pickerSelection} onChange={setPickerSelection} />
              <button
                type="button"
                className="btn primary"
                style={{ marginTop: 16 }}
                disabled={pickerSelection.length === 0 || setCompetencies.isPending}
                onClick={() => setCompetencies.mutate(pickerSelection)}
              >
                {setCompetencies.isPending ? "Generating…" : "Set competencies & generate questions"}
              </button>
            </div>
          ) : (
            <QuestionPlanEditor
              key={selectedReqId}
              requisitionId={selectedReqId}
              competencies={competencies}
              onDraftChange={(questions) => {
                setDraft(questions);
                setDirty(true);
                setStatus(null);
              }}
              onRegenerated={(questions, appliedTo) => {
                setDraft(questions);
                setDirty(false);
                setStatus({
                  tone: "good",
                  text:
                    "Regenerated and saved — edit, add or remove questions, then Save." +
                    (appliedTo > 0
                      ? ` ${appliedTo} invited candidate${appliedTo === 1 ? "" : "s"} who hadn't started will be asked these instead of their earlier set.`
                      : ""),
                });
              }}
            />
          )}
        </div>

        {/* Footer */}
        <div className="questions-modal-footer">
          <span className="questions-footer-hint">
            {status ? (
              <span style={{ color: status.tone === "good" ? "var(--teal)" : "var(--crit)", fontWeight: 600 }}>
                {status.tone === "good" && <Check style={{ width: 13, height: 13, verticalAlign: "-2px", marginRight: 4 }} />}
                {status.text}
              </span>
            ) : (
              "Configured questions will be presented whenever an AI interview is initiated."
            )}
          </span>
          <div className="questions-footer-actions">
            <button type="button" className="btn ghost" onClick={onClose}>
              Cancel
            </button>
            <button
              type="button"
              className="btn primary"
              disabled={!hasValidQuestions || save.isPending}
              onClick={() => {
                if (dirty && draft) {
                  save.mutate(draft);
                  return;
                }
                // Nothing edited since the last store (e.g. straight after Regenerate, which saves
                // as "generated"). Re-PUTting would store it as "manual" and switch per-candidate
                // tailoring off, so confirm and close instead.
                setStatus({ tone: "good", text: "Saved." });
                setTimeout(() => {
                  setStatus(null);
                  onClose();
                }, 700);
              }}
            >
              {save.isPending ? "Saving…" : "Save questions"}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
